import { Eye, EyeOff } from 'lucide-react';
import { useState } from 'react';

import { growdash_logo, loginpage_bg } from '@growdash/design-system/assets';
import { Button, Label } from '@growdash/design-system/atoms';
import { RHFInput } from '@growdash/design-system/molecules/index.js';

interface AuthProps {
  isSubmitting: boolean;
  errorMessage: string;
  handleForgotPassword: () => void;
  handleSignUp: () => void;
}

export default function Auth({
  isSubmitting,
  errorMessage,
  handleForgotPassword,
  handleSignUp
}: AuthProps) {
  const [showPassword, setShowPassword] = useState(false);

  return (
    <>
      <div className='container max-md:flex max-md:items-center relative h-lvh bg-white flex-col items-center justify-center md:grid lg:max-w-none lg:grid-cols-2 lg:px-0 force-light-mode'>
        <div className='relative hidden h-full flex-col bg-white p-10 text-foreground lg:flex'>
          <div className='absolute inset-0'>
            <img src={loginpage_bg} alt='Aisha' className='h-full w-full' />
          </div>
          <div className='relative z-20 flex items-center text-lg font-medium text-[#070086]'>
            <img
              src={growdash_logo}
              alt='growdash'
              className='h-12 w-auto mr-2'
            />
          </div>
          <div className='relative z-20 mt-auto'>
            <blockquote className='space-y-2'>
              <p className='text-lg text-bg_background'>
                Trusted by amazing restaurants
              </p>
            </blockquote>
          </div>
        </div>
        <div className='lg:p-8 bg-white'>
          <div className='mx-auto flex w-full flex-col justify-center space-y-6 sm:w-[350px]'>
            <div className='flex flex-col space-y-2 text-center'>
              <h1 className='text-2xl font-semibold tracking-tight text-[#070086]'>
                Welcome back
              </h1>
              <p className='text-sm text-muted-foreground'>
                Enter your credentials to sign in to your account
              </p>
            </div>
            <div className='grid gap-6'>
              <div className='grid gap-2'>
                <div className='grid gap-1'>
                  <Label className='sr-only' htmlFor='email'>
                    Email
                  </Label>
                  <RHFInput
                    name='email'
                    placeholder='name@example.com'
                    type='email'
                    disabled={isSubmitting}
                    className='text-black'
                  />
                </div>
                <div className='grid gap-1'>
                  <Label className='sr-only' htmlFor='password'>
                    Password
                  </Label>
                  <div className='relative'>
                    <RHFInput
                      name='password'
                      placeholder='Enter your password'
                      type={showPassword ? 'text' : 'password'}
                      disabled={isSubmitting}
                      className='w-full pr-10 text-black'
                    />
                    <button
                      type='button'
                      onClick={() => setShowPassword(!showPassword)}
                      className='absolute inset-y-0 right-0 pr-3 flex items-center text-muted-foreground hover:text-foreground'
                    >
                      {showPassword ? (
                        <EyeOff className='h-5 w-5' />
                      ) : (
                        <Eye className='h-5 w-5' />
                      )}
                    </button>
                  </div>
                </div>

                <Button
                  className='bg-[#070086] text-white hover:bg-[#070086]/90'
                  type='submit'
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Signing in...' : 'Sign In'}
                </Button>

                <Button
                  variant='link'
                  type='button'
                  onClick={handleForgotPassword}
                  className='px-0 justify-start font-normal text-[#070086] hover:text-[#070086]/80'
                >
                  Forgot password?
                </Button>
                <label className='text-red-500' htmlFor='remember-me'>
                  {errorMessage}
                </label>

                <div className='relative'>
                  <div className='absolute inset-0 flex items-center'>
                    <span className='w-full border-t border-muted-foreground' />
                  </div>
                  <div className='relative flex justify-center text-xs uppercase'>
                    <span className='bg-white px-2 text-muted-foreground'>
                      Or
                    </span>
                  </div>
                </div>

                <Label className='font-normal text-black'>
                  Need an account?
                  <Button
                    className='text-[#070086] px-0 ml-1'
                    variant='link'
                    onClick={handleSignUp}
                    type='button'
                  >
                    Sign up here
                  </Button>
                </Label>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
